<template>
    <div
    v-for="(product, index) in parsedProducts"
    :key="index"
    class="product-card"
    :style="`animation-duration: ${(index / 5) + 2}s;`"
    @click="emit('cardClicked', product)">
        <div class="color-square-container">
            <div class="color-square" :style="{backgroundColor: colors[index]}"></div>
        </div>
        <div class="info-container">
            <div class="description-container">
                <div v-if="product.accountDescription" class="description">{{ product.accountDescription }}</div>
                <div
                :class="{
                    'account-number': product.accountDescription,
                    'description': !product.accountDescription
                }"
                v-mask="ACCOUNT_NUMBER">
                    {{ product.accountNumber }}
                </div>
            </div>
            <div class="balance">
                <component
                v-if="product.balanceIcon"
                class="balance-icon"
                :class="product.balanceIconColor"
                :is="balanceIconController(product.balanceIcon)" />
                <span>{{ product.balance }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
/* Aux */
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/** Props
@colors {array} - array with products colors
@products {array} - array with products
*/
const props = defineProps({
    colors: Array,
    products: Array
})

const emit = defineEmits(['cardClicked'])

const parsedProducts = computed(() => {
    return props.products.map(product => ({
        ...product,
        balance: amountFormat(Math.abs(product.balance), product.accountCurrency),
        balanceIcon: product.balance && product.balance !== 0 ? product.balance > 0 ? 'plus' : 'minus' : null,
        balanceIconColor: product.balance > 0 ? 'positive' : 'negative'
    }))
})

const balanceIconController = (icon) => {
    return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}
</script>

<style lang="scss" scoped>
.product-card {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;

    width: 100%;
    height: 80px;

    padding: 16px;
    border-radius: 8px;

    background-color: var(--secondary-light-4);
    box-shadow: var(--box-shadow-2);

    transform: translateX(100vw);
    animation-name: slide-left;
    animation-delay: 0.2s;
    animation-iteration-count: 1;
    animation-fill-mode: both;

    @keyframes slide-left {
        0% {
            transform: translateX(100vw);
        }
        100% {
            transform: translateX(0vw);
        }
    }

    .color-square-container {
        .color-square {
            width: 16px;
            height: 16px;

            border-radius: 50%;
        }
    }

    .info-container {
        width: calc(100% - 16px);

        .description-container {
            padding-bottom: 10px;

            .description {
                @include H7-Regular;
                line-height: 16px;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                color: var(--secondary-dark-4);
            }

            .account-number {
                @include H8-Regular;

                color: var(--secondary-dark-1);
            }
        }

        .balance {
            display: flex;
            align-items: center;
            gap: 3px;

            @include H8-Regular;

            color: var(--secondary-dark-1);

            svg.balance-icon {
                flex-shrink: 0;

                width: 8px;
                height: 8px;

                &.positive {
                    :deep(.change-color-fill) {
                        fill: var(--positive);
                    }
                }

                &.negative {
                    :deep(.change-color-fill) {
                        fill: var(--negative);
                    }
                }
            }
        }
    }
}
</style>
